import { ref } from 'vue';

import {
  ClauseSchemasService,
  type ClauseSchemaV1Dto,
  type ContractClauseV2Dto,
  ContractsService,
  type CreateClauseFieldValueV1Dto,
  type CreateContractClauseV1Dto,
  type PatchClauseFieldValueV1Dto,
  type PatchContractClauseV1Dto,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type { ConditionSimple } from '../conditionTypes';

export function useConditions() {
  const fetchCondition = async (id: string) => {
    try {
      return await ContractsService.contractClauseControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const createCondition = async (data: CreateContractClauseV1Dto) => {
    try {
      await ContractsService.contractClauseControllerV1CreateV1(data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const updateCondition = async (id: string, data: PatchContractClauseV1Dto) => {
    try {
      await ContractsService.contractClauseControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteCondition = async (id: string) => {
    try {
      await ContractsService.contractClauseControllerV1RemoveOneV1(id);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteConditions = async (ids: string[]) => {
    try {
      await ContractsService.contractClauseControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  return {
    fetchCondition,
    createCondition,
    updateCondition,
    deleteCondition,
    deleteConditions,
  };
}

export function useConditionTypes() {
  const fetchConditionSchemas = async (contractTypeId: string) => {
    try {
      return await ClauseSchemasService.clauseSchemaControllerV1FindManyByContractTypeV1(
        contractTypeId,
      );
    } catch (err) {
      console.log(err);
    }
  };

  return {
    fetchConditionSchemas,
  };
}

export function useConditionsConverter() {
  const fieldValuePairsForUpdate = ref<{ [key: string]: string }>({});

  const convertConditionDtoToConditionSimple = (item: ContractClauseV2Dto): ConditionSimple => {
    const {
      createdAt: _createdAt,
      modifiedAt: _modifiedAt,
      clauseSchema,
      contractId: _contractId,
      fieldValues,
      ...res
    } = item;

    const dynamicFields = fieldValues.reduce<Record<string, unknown>>((acc, fv) => {
      const key = fv.clauseSchemaField.label;

      fieldValuePairsForUpdate.value[key] = fv.id;
      acc[key] = fv.literal;
      return acc;
    }, {});

    return {
      ...res,
      conditionTypeId: clauseSchema.id,
      ...dynamicFields,
    };
  };

  const convertConditionDtoListToConditionSimpleList = (
    data: ContractClauseV2Dto[],
  ): ConditionSimple[] => {
    return data.map((item) => {
      return convertConditionDtoToConditionSimple(item);
    });
  };

  const getFieldValuesForCreate = (
    selectedConditionType: ClauseSchemaV1Dto,
    dynamicFields: { [key: string]: unknown },
  ): CreateClauseFieldValueV1Dto[] => {
    return selectedConditionType.fields.map((field) => {
      const value = dynamicFields[field.label];
      const literal = value === undefined ? null : (value as string | number | boolean);

      return {
        clauseSchemaFieldId: field.id,
        literal: literal,
      };
    });
  };

  const getFieldValuesForUpdate = (
    selectedConditionType: ClauseSchemaV1Dto,
    dynamicFields: { [key: string]: unknown },
  ): PatchClauseFieldValueV1Dto[] => {
    return selectedConditionType.fields.map((field) => {
      const value = dynamicFields[field.label];
      const literal = value === undefined ? null : (value as string | number | boolean);
      return {
        literal: literal,
        id: fieldValuePairsForUpdate.value[field.label],
      };
    });
  };

  const convertConditionSimpleToCreateConditionDto = (
    data: ConditionSimple,
    contractId: string,
    selectedConditionType: ClauseSchemaV1Dto,
  ): CreateContractClauseV1Dto => {
    const { conditionTypeId, validFrom, validTo, note, name, ...dynamicFields } = data;

    const fieldValues = getFieldValuesForCreate(selectedConditionType, dynamicFields);

    return {
      contractId,
      name,
      validFrom,
      validTo,
      note,
      clauseSchemaId: conditionTypeId,
      fieldValues,
    };
  };

  const convertConditionSimpleToUpdateConditionDto = (
    data: ConditionSimple,
    contractId: string,
    selectedConditionType: ClauseSchemaV1Dto,
  ): PatchContractClauseV1Dto => {
    const {
      conditionTypeId: _conditionTypeId,
      validFrom,
      validTo,
      note,
      version,
      name,
      ...dynamicFields
    } = data;

    const fieldValues = getFieldValuesForUpdate(selectedConditionType, dynamicFields);

    return {
      contractId,
      name,
      validFrom,
      validTo,
      note,
      version,
      fieldValues,
    };
  };

  return {
    convertConditionDtoToConditionSimple,
    convertConditionDtoListToConditionSimpleList,
    convertConditionSimpleToCreateConditionDto,
    convertConditionSimpleToUpdateConditionDto,
  };
}
